// Cascade-testing candidates (pure). See plan §5.4 + PLAN-cascade-priors-
// inheritance-aware.md. From each "index" for a disease (affected, or a
// tested/obligate carrier) we walk out to unaffected relatives and state what is
// known about their carrier risk.
//
// WHAT THIS EMITS (TGCLOUD-1712 W4.7, decision DR-6). INFORMATION, not
// instructions. Every candidate carries a `derivation` the UI can render inline
// — "first-degree relative (parent) of a confirmed carrier, autosomal dominant —
// 1 in 2" — and an exact, unrounded probability. It never says "test this
// person". `recommendedAction` survives only as a frozen legacy alias; see
// LEGACY FIELDS at the bottom of this file.
//
// The prior is inheritance-mode-aware (cascadePriors): dominant families use the
// 0.5/0.25/0.125 degree ladder; autosomal-recessive families use carrier math
// (obligate parents/children, 2/3 full sibs, 1/2 half sibs). X-linked,
// mitochondrial and Y-linked families get NO prior at all rather than a
// sex-blind dominant one (W4.1 / DR-4). Probabilities are also conditioned on
// tested intermediates: a relative reached only through a confirmed non-carrier
// is pruned. Minors — and anyone whose age is unknown — are deferred.

import { RECOMMENDED_ACTION, isCarrierMark } from '../constants';
import { isAffectedBy } from './inheritance.helpers';
import {
  resolveCascadeMode,
  cascadePrior,
  NO_PRIOR_REASON,
  CASCADE_DEPTH_CAP
} from '../cascadePriors';
import {
  GENOTYPE_RESULT,
  RESULT_BASIS,
  isDefinitiveResult,
  resultForGene,
  classifyGenotypeResult,
  normaliseGene
} from '../carrierStatusVocabulary';

// ---------------------------------------------------------------------------
// RISK CONTROL RC-PAED-1 — paediatric deferral (TGCLOUD-1712 W4.8, DR-5)
// ---------------------------------------------------------------------------
//
// Predictive testing of a minor for an adult-onset condition is deferred. This
// is a BLANKET rule on the CANDIDATE'S OWN AGE and nothing else. There is NO
// onset-awareness anywhere in this module and none is intended: the rule does
// not know, and must not be read as knowing, whether the condition is
// paediatric- or adult-onset. Do not "improve" it into onset logic without a
// clinical decision to do so.
//
// An UNKNOWN age now defers too. The old test was
// `ageAtAssessment != null && ageAtAssessment < 18`, so a child with no recorded
// date of birth — the commonest data state for a relative added from a family
// history — was never deferred. Absence of a date is not evidence of adulthood.
// Pinned by a direct test in cascadePriors.test.js.
const MINOR_AGE = 18;
const DEFER_REASON = Object.freeze({
  minor: 'minor',
  ageUnknown: 'age_unknown'
});

function ageDeferral(ind) {
  const age = ind.ageAtAssessment;
  if (age == null)
    return {
      deferred: true,
      reason: DEFER_REASON.ageUnknown,
      control: 'RC-PAED-1',
      summary:
        'age not recorded — deferred until age is known (predictive testing of ' +
        'a minor is deferred, and an absent date of birth cannot rule that out)'
    };
  if (age < MINOR_AGE)
    return {
      deferred: true,
      reason: DEFER_REASON.minor,
      control: 'RC-PAED-1',
      summary: `under ${MINOR_AGE} — predictive testing deferred`
    };
  return { deferred: false, reason: null, control: 'RC-PAED-1', summary: null };
}

const carrierState = (ind, diseaseId) =>
  ind.carrierByDisease ? ind.carrierByDisease[diseaseId] : undefined;

const isKnownCarrier = (ind, diseaseId) =>
  isCarrierMark(carrierState(ind, diseaseId));

// A SEED index: affected, or carrying a projected gene→disease carrier mark.
const isSeedIndex = (ind, diseaseId) =>
  isAffectedBy(ind, diseaseId) || isKnownCarrier(ind, diseaseId);

const get = (ped, id) => (id ? ped.byPid.get(id) : null);

const positiveFor = (ind, gene) => {
  const r = resultForGene(ind, gene);
  return !!r && r.result === GENOTYPE_RESULT.positive;
};

// A confirmed non-carrier for `gene` — used to prune transmission through them.
//
// With no identified family gene we cannot say that any negative result excludes
// THIS family's variant, so we must not prune: dropping a relative removes them
// from the cascade entirely, and the fail-safe direction for a removal is to
// keep them. (TGCLOUD-1712 CT-3 — was `if (!gene) return true`, i.e. a negative
// result in ANY gene pruned the whole path whenever the family had zero or ≥2
// implicated genes.)
//
// Pruning is the one place a VARIANT-derived negative is deliberately NOT
// enough: only an explicit clinician `carrierStatus` negative prunes. A benign
// classification on a variant record says that variant is benign — not that the
// family's pathogenic variant was looked for and absent — and pruning is the
// unsafe direction (W4.3).
function isTestedNegativeForGene(ind, gene) {
  if (!ind || !gene) return false;
  const wanted = normaliseGene(gene);
  return (ind.genotypes || []).some((g) => {
    if (normaliseGene((g && (g.symbol || g.gene)) || '') !== wanted)
      return false;
    const r = classifyGenotypeResult(g);
    return (
      r.result === GENOTYPE_RESULT.negative &&
      r.basis === RESULT_BASIS.carrierStatus
    );
  });
}

// The family's implicated gene(s) for a disease, from index genotypes whose
// result asserts the variant is PRESENT. Exactly one → a targeted scope;
// several/none → panel.
//
// A confirmed-negative result says the index does NOT carry that gene, so it
// must not implicate it (TGCLOUD-1712 CT-2): it used to, which pointed a
// targeted test at a gene the family index had tested negative for, and could
// also flip other candidates from panel to targeted by making an ambiguous gene
// set look singular.
//
// The status vocabulary is now the SHARED one (W4.4), so `homozygote`,
// `hemizygote` and `normal` are finally classified here — a hemizygous male, the
// canonical X-linked positive, used to implicate nothing at all. A pathogenic
// variant with an unset carrier status also counts, via classifyGenotypeResult.
function familyGenes(indexes) {
  const genes = new Set();
  for (const ind of indexes)
    for (const g of ind.genotypes) {
      const r = classifyGenotypeResult(g);
      if (!r.gene) continue;
      if (r.result !== GENOTYPE_RESULT.positive) continue;
      genes.add(g.gene);
    }
  return [...genes];
}

// ---------------------------------------------------------------------------
// Sibship: full vs half (TGCLOUD-1712 CT-1 / W4.6)
// ---------------------------------------------------------------------------
//
// `tokenise.js` builds `siblingIds` as "shares AT LEAST ONE parent" and every
// entry used to be tagged `fullSib` at degree 1. A half-sibling of a dominant
// index therefore got 0.5 (correct answer 0.25) and of an AR index the full-sib
// 2/3 (correct answer 1/2). We do not need tokenise to change: the distinction
// is derivable here from fatherId/motherId.
//
// Half-siblings are emitted at DEGREE 2 — they share ~25% of their genome, which
// is what the degree ladder prices — and their `via` is the SHARED PARENT, so a
// negative result on that parent prunes them correctly.
//
// AMBIGUITY. One shared parent plus an unrecorded second parent on either side
// cannot be told apart from a full sibship with a missing parent record. Those
// are treated as FULL siblings (`certainty: 'assumed_full'`), because the
// conservative direction for a cascade is the one that keeps a relative in view
// at the higher risk, and the assumption is recorded on the candidate so the UI
// can qualify it.
const SIBSHIP_CERTAINTY = Object.freeze({
  recorded: 'recorded',
  assumedFull: 'assumed_full'
});

function sibship(a, b) {
  const shared = [];
  if (a.fatherId && a.fatherId === b.fatherId) shared.push(a.fatherId);
  if (a.motherId && a.motherId === b.motherId) shared.push(a.motherId);
  const bothFullyRecorded =
    a.fatherId && a.motherId && b.fatherId && b.motherId;
  if (shared.length >= 2)
    return { half: false, shared, certainty: SIBSHIP_CERTAINTY.recorded };
  if (shared.length === 1 && bothFullyRecorded)
    return { half: true, shared, certainty: SIBSHIP_CERTAINTY.recorded };
  return { half: false, shared, certainty: SIBSHIP_CERTAINTY.assumedFull };
}

// { full: [ind], half: [{ ind, sharedParent }] } for one individual.
function splitSibs(ped, ind) {
  const full = [];
  const half = [];
  for (const sid of ind.siblingIds) {
    const s = ped.byPid.get(sid);
    if (!s) continue;
    const rel = sibship(ind, s);
    if (rel.half) half.push({ ind: s, sharedParent: rel.shared[0], rel });
    else full.push({ ind: s, sharedParent: null, rel });
  }
  return { full, half };
}

// Relatives of `index` to 3rd degree, each tagged with its kind, degree, and the
// intermediate on the path (`via`) used for transmission conditioning. `via` is
// null for first-degree relatives.
function enumerateRelatives(ped, index) {
  const out = [];
  const seen = new Set([index.pid]);
  const add = (pid, kind, degree, via, extra) => {
    if (pid == null || seen.has(pid)) return null;
    const ind = ped.byPid.get(pid);
    if (!ind) return null;
    seen.add(pid);
    out.push({ pid, kind, degree, via, ...(extra || {}) });
    return ind;
  };

  // Degree 1
  const parents = [];
  for (const pid of [index.fatherId, index.motherId]) {
    const p = add(pid, 'parent', 1, null);
    if (p) parents.push(p);
  }
  const children = [];
  for (const cid of index.childrenIds) {
    const c = add(cid, 'child', 1, null);
    if (c) children.push(c);
  }

  // Siblings — full at degree 1, half at degree 2 via the shared parent.
  const indexSibs = splitSibs(ped, index);
  const fullSibs = [];
  for (const { ind: s, rel } of indexSibs.full) {
    const added = add(s.pid, 'fullSib', 1, null, { sibship: rel });
    if (added) fullSibs.push(added);
  }
  const halfSibs = [];
  for (const { ind: s, sharedParent, rel } of indexSibs.half) {
    const added = add(s.pid, 'halfSib', 2, sharedParent, { sibship: rel });
    if (added) halfSibs.push({ ind: added, via: sharedParent });
  }

  // Degree 2 (via a first-degree relative)
  const grandparents = [];
  for (const p of parents)
    for (const gpid of [p.fatherId, p.motherId]) {
      const gp = add(gpid, 'grandparent', 2, p.pid);
      if (gp) grandparents.push({ ind: gp, via: p.pid });
    }
  // A parent's full sibling is an aunt/uncle (2°); a parent's HALF sibling is a
  // half-aunt/uncle (3°) — same CT-1 error one generation up.
  const fullAuntsUncles = [];
  for (const p of parents) {
    const ps = splitSibs(ped, p);
    for (const { ind: a, rel } of ps.full) {
      const added = add(a.pid, 'auntUncle', 2, p.pid, { sibship: rel });
      if (added) fullAuntsUncles.push({ ind: added, via: p.pid });
    }
    for (const { ind: a, rel } of ps.half)
      add(a.pid, 'halfAuntUncle', 3, p.pid, { sibship: rel });
  }
  const grandchildren = [];
  for (const c of children)
    for (const gcid of c.childrenIds) {
      const gc = add(gcid, 'grandchild', 2, c.pid);
      if (gc) grandchildren.push({ ind: gc, via: c.pid });
    }
  const niblings = [];
  for (const s of fullSibs)
    for (const nid of s.childrenIds) {
      const nn = add(nid, 'nieceNephew', 2, s.pid);
      if (nn) niblings.push({ ind: nn, via: s.pid });
    }
  // Children of a half-sibling are 3° (half-niece/nephew), conditioned on the
  // same shared parent as their half-sib parent.
  for (const { ind: s, via } of halfSibs)
    for (const nid of s.childrenIds) add(nid, 'halfNieceNephew', 3, via);

  // Degree 3 (carry the conditioning intermediate forward)
  for (const { ind: gp, via } of grandparents) {
    for (const ggid of [gp.fatherId, gp.motherId])
      add(ggid, 'greatGrandparent', 3, via);
    // Only a grandparent's FULL sibling is a great-aunt/uncle at 3°; a half one
    // is 4° and beyond the cap, so it is deliberately not enumerated.
    for (const { ind: ga, rel } of splitSibs(ped, gp).full)
      add(ga.pid, 'greatAuntUncle', 3, via, { sibship: rel });
  }
  // First cousins are the children of a FULL aunt/uncle (3°). Children of a half
  // aunt/uncle are 4° — beyond the cap.
  for (const { ind: a, via } of fullAuntsUncles)
    for (const cid of a.childrenIds) add(cid, 'firstCousin', 3, via);
  for (const { ind: gc, via } of grandchildren)
    for (const ggcid of gc.childrenIds) add(ggcid, 'greatGrandchild', 3, via);
  for (const { ind: nn, via } of niblings)
    for (const gnid of nn.childrenIds) add(gnid, 'grandNieceNephew', 3, via);

  return out;
}

export function detectCascadeCandidates(
  ped,
  diseaseId,
  inheritancePattern = null
) {
  const seeds = ped.individuals.filter((i) => isSeedIndex(i, diseaseId));
  if (seeds.length === 0) return [];

  // W4.2: the whole pricing decision, not just a mode id — what the panel above
  // DISPLAYS, what actually priced these numbers, and whether those differ.
  const pricing = resolveCascadeMode(inheritancePattern);
  const genes = familyGenes(seeds);
  const targetGene = genes.length === 1 ? genes[0] : null;

  // INDEX PROMOTION (W4.4). Anyone with a POSITIVE result in a gene the family
  // is implicated for is an index in their own right — their relatives are at
  // risk through them. Without this, a hemizygous male (the canonical X-linked
  // positive) created no index at all: `hemizygote` was in none of the three old
  // status sets, so unless he was separately recorded as affected the cascade
  // simply did not radiate from him.
  //
  // One pass is enough: a promoted index carries a gene that is already in
  // `genes`, so promotion cannot widen the gene set and cannot cascade further.
  const indexPids = new Set(seeds.map((i) => i.pid));
  if (genes.length) {
    for (const ind of ped.individuals) {
      if (ind.isDummy || indexPids.has(ind.pid)) continue;
      if (genes.some((g) => positiveFor(ind, g))) indexPids.add(ind.pid);
    }
  }
  const indexes = ped.individuals.filter((i) => indexPids.has(i.pid));

  const eligible = (ind) =>
    !!ind &&
    !ind.isDummy &&
    !ind.deceased &&
    !isAffectedBy(ind, diseaseId) &&
    !isKnownCarrier(ind, diseaseId) &&
    !indexPids.has(ind.pid);

  // pid → best candidate (highest prior wins on dedupe across indexes/paths).
  const byPid = new Map();
  const consider = (ind, rel, indexAffected) => {
    if (!eligible(ind)) return;

    // Conditioning: prune if the connecting intermediate is a confirmed
    // non-carrier (the allele didn't pass that way). A positive intermediate is
    // itself an index, so its descendants are already covered.
    if (
      rel.via != null &&
      isTestedNegativeForGene(get(ped, rel.via), targetGene)
    )
      return;

    const priced = cascadePrior(pricing, {
      kind: rel.kind,
      degree: rel.degree,
      indexAffected
    });
    // A numeric 0 means the mode says this relative is not at risk. A NULL prior
    // means we decline to state one (unpriceable mode) — that is still a
    // candidate, shown with its relationship and mode and no percentage.
    if (priced.prior != null && !(priced.prior > 0)) return;

    // W4.3 GENOTYPE-AWARE SUPPRESSION. The individual's own result for the
    // target gene, read from BOTH the carrier-status dropdown and the variant
    // record — the dropdown alone defaults to empty, which is how a person with
    // a recorded PATHOGENIC variant was scored as untested and offered their own
    // test (the John Blythe defect).
    //
    // A DEFINITIVE result (positive or negative, from either channel) suppresses
    // the prior: `priorProbability` becomes null and `knownResult` carries what
    // is actually known, so the UI shows the result instead of a probability.
    // Note a positive result normally makes them an index above, so what reaches
    // here is overwhelmingly a negative.
    //
    // An INCONCLUSIVE result (VUS, or a self-contradicting record) does NOT
    // suppress: a VUS does not resolve carrier state, so the person remains at
    // their prior risk. It is surfaced as a qualifier instead.
    //
    // A result for a DIFFERENT gene is never consulted (resultForGene keys on
    // the target gene), and with no single target gene nothing is suppressed at
    // all — CT-3, the fail-safe direction.
    //
    // A result with NO DATE is honoured exactly like a dated one. Nothing here
    // branches on recency: refusing to honour an undated result would restore
    // the John Blythe defect (offering a test to someone with a known
    // pathogenic variant), which is the more dangerous of the two errors. The
    // field is plumbed through `knownResult.dateRecorded` for display, and is
    // null until tokenise.js carries `dateRecorded` through normaliseGenotype.
    const known = resultForGene(ind, targetGene);
    const resultKnown = !!known && isDefinitiveResult(known.result);
    const inconclusive =
      !!known && known.result === GENOTYPE_RESULT.inconclusive;

    const priorProbability = resultKnown ? null : priced.prior;
    const priorUnavailable = resultKnown
      ? {
          reason: NO_PRIOR_REASON.resultAlreadyKnown,
          mode: pricing.displayedMode,
          summary:
            `${
              known.result === GENOTYPE_RESULT.positive
                ? 'Positive'
                : 'Negative'
            } ` + `result already on file for ${known.gene} — no prior applies`
        }
      : priced.prior == null
      ? {
          reason: pricing.unsupportedReason,
          mode: pricing.displayedMode,
          summary: priced.derivation.summary
        }
      : null;

    const existing = byPid.get(ind.pid);
    // Nulls sort below every real number, so a stated prior always wins the
    // dedupe over an unstated one.
    const rank = (v) => (v == null ? -1 : v);
    if (existing && rank(existing.priorProbability) >= rank(priorProbability))
      return;

    const viaResultKnown =
      rel.via != null && !!resultForGene(get(ped, rel.via), targetGene);
    const isConditional =
      rel.via != null &&
      priced.derivation.assumesUnknownIntermediate &&
      !viaResultKnown;

    const deferral = ageDeferral(ind);

    // W4.9: qualifiers are collected in ONE place so a surface cannot render an
    // unqualified number by forgetting to read a boolean. `isConditional` and
    // `conditionalOn` were computed and then dropped by every consumer, so a
    // prior conditional on an untested intermediate rendered as if it were not.
    const qualifiers = [];
    if (priorUnavailable)
      qualifiers.push({
        code:
          priorUnavailable.reason === NO_PRIOR_REASON.resultAlreadyKnown
            ? 'result_already_known'
            : 'no_prior_available',
        summary: priorUnavailable.summary
      });
    if (isConditional)
      qualifiers.push({
        code: 'conditional_on_untested_intermediate',
        pid: rel.via,
        summary:
          'conditional on an intermediate relative who has not been tested — ' +
          'testing them first would resolve it'
      });
    if (inconclusive)
      qualifiers.push({
        code: 'inconclusive_result_on_file',
        gene: known.gene,
        summary:
          `an inconclusive result (${known.interpretation}) is on file for ` +
          `${known.gene}; it does not resolve carrier state`
      });
    if (known && known.conflict)
      qualifiers.push({
        code: 'result_conflict',
        gene: known.gene,
        summary:
          `the recorded carrier status and variant classification for ` +
          `${known.gene} disagree — review the record`
      });
    if (deferral.deferred)
      qualifiers.push({
        code: `deferred_${deferral.reason}`,
        summary: deferral.summary
      });
    if (pricing.modeDivergence)
      qualifiers.push({
        code: 'priced_without_the_displayed_mode',
        summary:
          `${
            priced.derivation.modeLabel || 'the displayed pattern'
          } is shown ` +
          'for this family, but it is not strongly enough supported to price ' +
          'with; this figure comes from the general degree relationship'
      });
    if (rel.sibship && rel.sibship.certainty === 'assumed_full')
      qualifiers.push({
        code: 'assumed_full_sibship',
        summary:
          'a parent is not recorded on one side, so this sibship is assumed ' +
          'full rather than half'
      });

    byPid.set(ind.pid, {
      pid: ind.pid,
      token: ind.token,
      degree: rel.degree,
      relationship: rel.kind,
      // EXACT, unrounded (W4.9). 2/3 stays 2/3 and 0.125 stays 0.125 —
      // Math.round(prior * 100) rendered 12.5% as "13%", a figure no genetics
      // reference produces. Formatting belongs to the UI. `null` = no prior.
      priorProbability,
      priorUnavailable,
      band: resultKnown ? 'unavailable' : priced.band,
      obligate: priced.band === 'obligate' && !resultKnown,
      // The inline sentence + its parts (DR-6).
      derivation: priced.derivation,
      // W4.2: nothing else can tell whether the mode on display priced this.
      pricing,
      isConditional,
      conditionalOn: rel.via != null ? rel.via : null,
      qualifiers,
      knownResult: known,
      deferral,
      sibship: rel.sibship || null,
      variant: { gene: targetGene, targetVariant: null },
      // The SCOPE a test would need, as a fact about the family's gene set —
      // not an instruction to order one. Null when the result is already known.
      testScope: resultKnown ? null : targetGene ? 'targeted' : 'panel',

      // ---- LEGACY FIELDS — frozen, do not use in new code ----
      //
      // `recommendedAction` is the imperative vocabulary W4.7 replaces. It is
      // retained ONLY so the existing display surfaces keep working during the
      // migration, and it is FROZEN at its old semantics on purpose: it defers
      // for a recorded minor and nothing else, so the new age-unknown deferral
      // does not silently change what three unmigrated surfaces render. Read
      // `deferral`, `testScope` and `derivation` instead. Delete this field —
      // and RECOMMENDED_ACTION with it — once the display surfaces have moved.
      recommendedAction:
        deferral.reason === DEFER_REASON.minor
          ? RECOMMENDED_ACTION.defer
          : targetGene
          ? RECOMMENDED_ACTION.targetedTest
          : RECOMMENDED_ACTION.panelTest,
      deferReason: deferral.reason
    });
  };

  for (const index of indexes) {
    const indexAffected = isAffectedBy(index, diseaseId);
    for (const rel of enumerateRelatives(ped, index)) {
      if (rel.degree > CASCADE_DEPTH_CAP) continue;
      consider(get(ped, rel.pid), rel, indexAffected);
    }
  }

  // Order: lower degree first, then descending prior (an unstated prior sorts
  // last within its degree), then youngest age.
  return [...byPid.values()].sort((a, b) => {
    if (a.degree !== b.degree) return a.degree - b.degree;
    const pa = a.priorProbability == null ? -1 : a.priorProbability;
    const pb = b.priorProbability == null ? -1 : b.priorProbability;
    if (pa !== pb) return pb - pa;
    const ageA = get(ped, a.pid).ageAtAssessment ?? Infinity;
    const ageB = get(ped, b.pid).ageAtAssessment ?? Infinity;
    return ageA - ageB;
  });
}
